import { useApp } from '@/contexts';

import { createElement, useEffect, useRef, useState } from 'react';

import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { PageHeading } from '@/components/layout';
import type { Credential, IntegrationProvider } from '@/types';
import { logger } from '@/utils/logger';

import {
  useDeleteCredentialMutation,
  useGetCredentialsQuery,
  useGetProviderMetadataMutation,
  useLazyCheckOAuthCredsQuery,
  useSaveCredentialsMutation,
  useTestConnectionMutation,
} from '../api/environmentsApi';
import { useContentfulConnectors } from '../hooks/useContentfulConnectors';
import { getConnectorIcon } from '../utils/connectorIcons';
import { getOAuthCredentialType, getOAuthProviderLabel } from '../utils/credentialTypeMapping';
import { getTestEndpointConfig } from '../utils/providerUtils';
import { CredentialSectionRouter } from './CredentialSections';
import { ManageEnvironmentVariables } from './ManageEnvironmentVariables';

// ─── Helper functions ───

/** Generate a default key from provider type. Matches dataProtect: LocalFile always uses key "LocalFile" */
const generateDefaultKey = (providerType: string, existingCredentials: Credential[]): string => {
  if (providerType === 'LocalFile') return 'LocalFile';
  const base = providerType.toLowerCase().replace(/[^a-z0-9]/g, '');
  const existingKeys = existingCredentials.map((c) => c.key);
  if (!existingKeys.includes(base)) return base;
  let counter = 2;
  while (existingKeys.includes(`${base}${counter}`)) counter++;
  return `${base}${counter}`;
};

// ─── Types ───

export type ConnectorFormMode = 'addSource' | 'addDest' | 'edit';

interface ConnectorFormProps {
  provider: IntegrationProvider;
  tenantId: string;
  envId: string;
  envName: string;
  existingCredential: Credential | null;
  existingCredentials: Credential[];
  onCancel: () => void;
  onSaved: () => void;
  /** Whether this is an add-source, add-destination, or edit form */
  formMode?: ConnectorFormMode;
  /** Wizard mode - show wizard progress steps */
  isWizard?: boolean;
  /** Current wizard step (1, 2, or 3) */
  wizardStep?: 1 | 2 | 3;
}

// ─── Styles matching legacy dataProtect app.less ───

/** Label color matches legacy `.labelz { color: #54698d }` */
const labelSx = { fontSize: '13px', fontWeight: 'normal', mb: 0.25, color: '#54698d' };

/**
 * Wizard progress step indicator
 * Matches legacy dataProtect Connector.js wizard progress bar
 */
const WizardStep = ({ step, label, active }: { step: number; label: string; active: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: active ? '#00853E' : '#e0e0e0',
        color: active ? '#fff' : '#666',
      }}
    >
      {step}
    </Box>
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: active ? 600 : 400,
        color: active ? '#00853E' : '#666',
      }}
    >
      {label}
    </Typography>
  </Box>
);

// ─── Main ConnectorForm component ───

export const ConnectorForm = ({
  provider,
  tenantId,
  envId,
  envName,
  existingCredential,
  existingCredentials,
  onCancel,
  onSaved,
  formMode = 'addSource',
  isWizard = false,
  wizardStep = 1,
}: ConnectorFormProps) => {
  const { appId } = useApp();
  const { showSnackbar } = useSnackbar();

  // Adapter connectors have metadataEndpoint "testconnection" or "adaptermetadata"
  // They show the credentials form (with adapter secret) even if no credential fields are defined
  // Matches legacy Connector.js: showProviderForm = (providerConfig.credentials) || isAdapter
  const isAdapter =
    provider.metadataEndpoint === 'testconnection' ||
    provider.metadataEndpoint === 'adaptermetadata';

  // Only show env vars for connectors with no credentials AND not adapters (like AmazonSQS)
  const hasNoCredentials =
    (!provider.credentials || provider.credentials.length === 0) && !isAdapter;

  // Get environment variables credential if it exists
  const { data: allCredentials } = useGetCredentialsQuery(
    { tenantId, appId, envName },
    { skip: !hasNoCredentials }
  );
  const envVarsCred = hasNoCredentials
    ? allCredentials?.find((c) => c.credentialType === 'EnvironmentVariables')
    : undefined;

  // Fetch Contentful connector data for logos
  const { connectors: contentfulConnectors } = useContentfulConnectors();

  // Helper to get Contentful logo URL
  const getConnectorLogoUrl = (): string | undefined => {
    const entry = contentfulConnectors.find((c) => c.fields.providerType === provider.type);
    return entry?.fields.logo?.fields?.file?.url;
  };

  const isEditMode = !!existingCredential;
  const [connectorName, setConnectorName] = useState(
    existingCredential?.connectionName || provider.label || provider.type
  );
  const [connectorKey, setConnectorKey] = useState(
    existingCredential?.key || generateDefaultKey(provider.type, existingCredentials)
  );
  const [credFields, setCredFields] = useState<Record<string, string>>(() => {
    if (existingCredential?.credentialsJson) {
      const fields: Record<string, string> = {};
      const json = existingCredential.credentialsJson;

      // For BVA/Zoom/Canvas: restructure token fields into nested object if needed
      if (
        provider.type === 'BrightSpaceValenceApi' ||
        provider.type === 'Zoom' ||
        provider.type === 'CanvasOAuth2'
      ) {
        const tokenKey =
          provider.type === 'Zoom'
            ? 'zoomToken'
            : provider.type === 'CanvasOAuth2'
              ? 'canvasToken'
              : 'bvaToken';

        // Check if token fields are flat (scope, refreshToken, accessToken at root)
        const hasFlat = 'scope' in json || 'refreshToken' in json || 'accessToken' in json;
        const hasNested = tokenKey in json && typeof json[tokenKey] === 'object';

        if (hasFlat && !hasNested) {
          // Restructure: move token fields into nested object
          const tokenObj: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(json)) {
            if (['scope', 'refreshToken', 'accessToken'].includes(key)) {
              tokenObj[key] = value;
            } else {
              fields[key] = String(value ?? '');
            }
          }
          // Store nested token object (OAuthKeyToggleSection expects this structure)
          fields[tokenKey] = tokenObj as unknown as string;
        } else {
          // Already nested or no tokens - preserve ONLY the tokenKey object, stringify others
          for (const [key, value] of Object.entries(json)) {
            if (key === tokenKey && typeof value === 'object') {
              // Preserve the token object structure
              fields[key] = value as unknown as string;
            } else {
              // Stringify everything else
              fields[key] = String(value ?? '');
            }
          }
        }
      } else {
        // Other provider types: flatten normally
        for (const [key, value] of Object.entries(json)) {
          fields[key] = String(value ?? '');
        }
      }

      // SFTP: ensure filePattern has default
      if (provider.type === 'SFTP' && !fields['filePattern']) {
        fields['filePattern'] = '*.*';
      }
      return fields;
    }
    // New connector defaults (matches credSections/index.js componentWillMount lines 68-96)
    const fields: Record<string, string> = {};
    if (provider.type === 'SFTP') {
      fields['credentialType'] = 'password';
      fields['filePattern'] = '*.*';
    } else if (provider.type === 'LingkWebSocket') {
      fields['rootUrl'] = 'wss://';
    } else if (
      provider.type === 'BrightSpaceValenceApi' ||
      provider.type === 'Zoom' ||
      provider.type === 'CanvasOAuth2'
    ) {
      fields['authenticationType'] = 'key';
    } else {
      fields['rootUrl'] = 'https://';
    }
    return fields;
  });
  const [isDefault, setIsDefault] = useState(existingCredential?.defaultConnection ?? false);
  const [isApiAccessible, setIsApiAccessible] = useState(
    existingCredential?.ApiAccessible ?? false
  );
  const [showPass, setShowPass] = useState(false);

  // Connection test result states (for persistent error banner like old UI)
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionSuccess, setConnectionSuccess] = useState(false);

  // Timeout ref for cleanup
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // API mutations
  const [saveCredentials, { isLoading: isSaving }] = useSaveCredentialsMutation();
  const [deleteCredential, { isLoading: isDeleting }] = useDeleteCredentialMutation();
  const [testConnection, { isLoading: isTesting }] = useTestConnectionMutation();
  const [getProviderMetadata] = useGetProviderMetadataMutation();
  const [fetchExistingCred] = useLazyCheckOAuthCredsQuery();

  // Auto-default first connection of this type
  const isFirstOfType = !existingCredentials.some(
    (c) => c.credentialType === provider.credentialType || c.credentialType === provider.type
  );

  useEffect(() => {
    if (isFirstOfType && !isEditMode) setIsDefault(true);
  }, [isFirstOfType, isEditMode]);

  // Cleanup redirect timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // Auto-test connection on mount for existing connectors
  // Matches dataProtect ConnectorSection.js checkOauthCreds() + check():
  // - For OAuth providers: fetch fresh credentials via checkOAuthCreds (decrypted, with JWT)
  // - For non-OAuth: use existing credentialsJson from list API
  // This is required because the list API returns redacted credentialsJson for OAuth providers.
  useEffect(() => {
    if (!isEditMode) return;
    if (provider.noCheckConnectionYet) return;

    const endpointConfig = getTestEndpointConfig(provider, envId);
    if (!endpointConfig) return;

    // Check if provider uses OAuth (matches dataProtect CredSections.js line 71-72)
    const isOAuth =
      provider.credentials &&
      provider.credentials.length === 1 &&
      provider.credentials[0] === 'Oauth';

    let cancelled = false;

    const runAutoTest = async () => {
      try {
        let credsToTest: Record<string, unknown> | undefined;

        if (isOAuth) {
          // OAuth providers: fetch fresh credentials (includes decrypted tokens + JWT)
          // Matches dataProtect ConnectorSection.js checkOauthCreds() line 677-685
          const providerLabel = getOAuthProviderLabel(provider.type);
          const freshCred = await fetchExistingCred({
            tenantId,
            appId,
            providerType: providerLabel,
            envName,
            connectionKey: connectorKey,
          }).unwrap();

          if (cancelled) return;
          credsToTest = freshCred?.credentialsJson as Record<string, unknown> | undefined;
        } else {
          // Non-OAuth: use credentials from list API
          credsToTest = existingCredential?.credentialsJson as Record<string, unknown> | undefined;
        }

        // Skip test if credentials are empty
        if (!credsToTest || Object.keys(credsToTest).length === 0) return;

        await testConnection({
          tenantId,
          appId,
          type: provider.type,
          endpoint: endpointConfig.endpoint,
          envId: endpointConfig.envId,
          envName,
          connectorKey,
          data:
            endpointConfig.endpoint === 'sfAgent'
              ? undefined
              : {
                  credentialType: provider.credentialType || provider.type,
                  credentialsJson: credsToTest,
                  providerLabel: provider.type,
                },
        }).unwrap();
        if (!cancelled) setConnectionSuccess(true);
      } catch (testErr: unknown) {
        if (cancelled) return;
        let errorMessage = 'Connection test failed';
        if (testErr && typeof testErr === 'object' && 'data' in testErr) {
          const errData = (testErr as { data: unknown }).data;
          if (typeof errData === 'string') {
            errorMessage = errData;
          } else if (errData && typeof errData === 'object') {
            const errObj = errData as Record<string, unknown>;
            errorMessage =
              (errObj.error as string) ||
              (errObj.message as string) ||
              (errObj.Message as string) ||
              errorMessage;
          }
        } else if (testErr && typeof testErr === 'object' && 'message' in testErr) {
          errorMessage = (testErr as { message: string }).message;
        }
        setConnectionError(errorMessage);
      }
    };

    runAutoTest();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (name: string, value: string) => {
    setCredFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setConnectionError(null);
    setConnectionSuccess(false);

    const endpointConfig = getTestEndpointConfig(provider, envId);
    const isSfAgent = endpointConfig?.endpoint === 'sfAgent';
    const oauthCredType = getOAuthCredentialType(provider.type);
    const credType = oauthCredType || provider.credentialType || provider.type;

    // For sfAgent OAuth connectors, merge form fields into the existing credential
    // so that server-stored fields (consumerKey, tokens, isSandbox, etc.) are preserved.
    let finalCredFields = credFields as Record<string, unknown>;
    if (isSfAgent) {
      try {
        const providerLabel = getOAuthProviderLabel(provider.type);
        const existing = await fetchExistingCred({
          tenantId,
          appId,
          providerType: providerLabel,
          envName,
          connectionKey: connectorKey,
        }).unwrap();
        if (existing?.credentialsJson) {
          const existingJson = existing.credentialsJson as Record<string, unknown>;
          finalCredFields = {
            ...existingJson,
            ...credFields,
          };
          // The form state stringifies ALL values (Record<string, string>).
          // Restore original types for fields the backend asserts at runtime:
          // salesforce_agent does envJson["isSandbox"].(bool) — panics on string.
          for (const [k, v] of Object.entries(existingJson)) {
            if (typeof v === 'boolean' && typeof finalCredFields[k] === 'string') {
              finalCredFields[k] = finalCredFields[k] === 'true';
            } else if (typeof v === 'number' && typeof finalCredFields[k] === 'string') {
              const n = Number(finalCredFields[k]);
              if (!isNaN(n)) finalCredFields[k] = n;
            }
          }
          // Remove form-only key variant (isSandBox capital B) that doesn't exist in backend
          if ('isSandBox' in finalCredFields && 'isSandbox' in finalCredFields) {
            delete finalCredFields.isSandBox;
          }
        }
      } catch {
        // Existing credential not found (first save) — use form fields as-is
      }
    }

    try {
      await saveCredentials({
        tenantId,
        appId,
        envId,
        key: connectorKey,
        data: {
          key: connectorKey,
          providerLabel: provider.type,
          credentialType: credType,
          credentialsJson: finalCredFields,
          connectionName: connectorName,
          defaultConnection: isDefault,
          ApiAccessible: isApiAccessible,
        },
      }).unwrap();

      // Determine if this is an adapter (skipCheckConnection=true but has metadataEndpoint)
      const isAdapter =
        provider.metadataEndpoint === 'testconnection' ||
        provider.metadataEndpoint === 'adaptermetadata';

      // Test connection after save (same order as dataProtect)
      if (!provider.noCheckConnectionYet) {
        // For adapters: metadata fetch IS the connection test
        if (isAdapter && provider.metadataEndpoint) {
          try {
            await getProviderMetadata({
              tenantId,
              appId,
              metadataEndpoint: provider.metadataEndpoint,
              metadataApi: provider.metadataApi,
              credentialType: provider.credentialType,
              credentialsJson: finalCredFields,
              providerType: provider.type,
              envName,
              connectionKey: provider.credentials?.includes('Oauth') ? connectorKey : undefined,
            }).unwrap();

            setConnectionSuccess(true);
            showSnackbar('Connection successful!', 'success');
            redirectTimeoutRef.current = setTimeout(() => {
              onSaved();
            }, 1500);
          } catch (testErr: unknown) {
            let errorMessage =
              'Connection test failed. Credentials saved but connection could not be verified.';
            if (testErr && typeof testErr === 'object' && 'data' in testErr) {
              const errData = (testErr as { data: unknown }).data;
              if (typeof errData === 'string') {
                errorMessage = errData;
              } else if (errData && typeof errData === 'object') {
                const errObj = errData as Record<string, unknown>;
                errorMessage =
                  (errObj.error as string) ||
                  (errObj.message as string) ||
                  (errObj.Message as string) ||
                  errorMessage;
              }
            } else if (testErr && typeof testErr === 'object' && 'message' in testErr) {
              errorMessage = (testErr as { message: string }).message;
            }
            setConnectionError(errorMessage);
            showSnackbar(errorMessage, 'error');
          }
        } else if (endpointConfig) {
          // For non-adapters: use regular testConnection
          try {
            await testConnection({
              tenantId,
              appId,
              type: provider.type,
              endpoint: endpointConfig.endpoint,
              envId: endpointConfig.envId,
              envName,
              connectorKey,
              data: isSfAgent
                ? undefined
                : {
                    credentialType: credType,
                    credentialsJson: finalCredFields,
                    providerLabel: provider.type,
                  },
            }).unwrap();

            setConnectionSuccess(true);
            showSnackbar('Connection successful!', 'success');
            redirectTimeoutRef.current = setTimeout(() => {
              onSaved();
            }, 1500);
          } catch (testErr: unknown) {
            let errorMessage =
              'Connection test failed. Credentials saved but connection could not be verified.';
            if (testErr && typeof testErr === 'object' && 'data' in testErr) {
              const errData = (testErr as { data: unknown }).data;
              if (typeof errData === 'string') {
                errorMessage = errData;
              } else if (errData && typeof errData === 'object') {
                const errObj = errData as Record<string, unknown>;
                errorMessage =
                  (errObj.error as string) ||
                  (errObj.message as string) ||
                  (errObj.Message as string) ||
                  errorMessage;
              }
            } else if (testErr && typeof testErr === 'object' && 'message' in testErr) {
              errorMessage = (testErr as { message: string }).message;
            }
            setConnectionError(errorMessage);
            showSnackbar(errorMessage, 'error');
          }
        } else {
          // No endpoint config - skip test and redirect
          onSaved();
        }
      } else {
        onSaved();
      }
    } catch (err: unknown) {
      // Extract error message from save failure
      let errorMessage = 'Failed to save credentials';

      // Handle RTK Query error format
      if (err && typeof err === 'object' && 'data' in err) {
        const errData = (err as { data: unknown }).data;
        if (typeof errData === 'string') {
          errorMessage = errData;
        } else if (errData && typeof errData === 'object') {
          // Check for common error fields
          const errObj = errData as Record<string, unknown>;
          errorMessage =
            (errObj.error as string) ||
            (errObj.message as string) ||
            (errObj.Message as string) || // Backend sometimes uses PascalCase
            errorMessage;
        }
      } else if (err && typeof err === 'object' && 'message' in err) {
        // Standard Error object
        errorMessage = (err as { message: string }).message;
      }

      // Show error via global snackbar
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async () => {
    if (!existingCredential) return;

    // Skip delete for local-only credentials (never saved to backend)
    if (existingCredential.wasJustAdded) {
      setDeleteDialogOpen(false);
      onSaved();
      return;
    }

    // Skip delete for corrupted credentials with empty keys
    // (these are legacy data that need manual DB cleanup)
    if (!existingCredential.key) {
      showSnackbar('Cannot delete: credential has no key (corrupted data)', 'error');
      setDeleteDialogOpen(false);
      return;
    }

    try {
      await deleteCredential({
        tenantId,
        appId,
        providerLabel: existingCredential.credentialType,
        connectionKey: existingCredential.key,
        environmentId: envId,
      }).unwrap();
      setDeleteDialogOpen(false);
      onSaved();
    } catch (err) {
      logger.error('Failed to delete credential:', err);
      showSnackbar('Failed to delete credential', 'error');
    }
  };

  const isFormValid = connectorName.trim().length > 0 && connectorKey.trim().length > 0;

  // Determine page title based on form mode
  const pageTitle = isEditMode
    ? `Edit ${provider.label || provider.type} Connector`
    : formMode === 'addSource'
      ? `Add Source: ${provider.label || provider.type}`
      : `Add Destination: ${provider.label || provider.type}`;

  const pageSubtitle = isEditMode
    ? 'Update connector credentials and configuration'
    : 'Configure connection settings to integrate with this system';

  // ─── Render ───

  // If connector has no credentials, show environment variables interface directly
  // Matches old UI behavior where connectors without credentials field show envVars
  if (hasNoCredentials) {
    return (
      <Box sx={{ width: '100%' }}>
        {/* Page Heading */}
        <PageHeading
          title={pageTitle}
          subtitle="This connector uses environment variables for configuration"
        />

        {/* Wizard progress indicator */}
        {isWizard && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              px: 1,
            }}
          >
            <WizardStep step={1} label="Connect Source" active={wizardStep === 1} />
            <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
            <WizardStep step={2} label="Connect Destination" active={wizardStep === 2} />
            <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
            <WizardStep step={3} label="Create a Recipe" active={wizardStep === 3} />
          </Box>
        )}

        {/* Breadcrumb navigation */}
        <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 18 }} />} sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            onClick={onCancel}
            sx={{ fontSize: '14px', color: '#54698d', cursor: 'pointer' }}
          >
            {envName}
          </Link>
          <Link
            component="button"
            underline="hover"
            onClick={onCancel}
            sx={{ fontSize: '14px', color: '#54698d', cursor: 'pointer' }}
          >
            Connections
          </Link>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {(() => {
              const logoUrl = getConnectorLogoUrl();
              return logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt=""
                  sx={{ width: 24, height: 24, objectFit: 'contain' }}
                />
              ) : (
                getConnectorIcon(provider.label || provider.type, provider.type)
              );
            })()}
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
              {provider.label || provider.type}
            </Typography>
          </Box>
        </Breadcrumbs>

        {/* Connection Error Banner (matches old UI red error banner) */}
        {connectionError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: '13px',
                wordBreak: 'break-word',
              },
            }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setConnectionError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {connectionError}
          </Alert>
        )}

        {/* Help text */}
        <Typography sx={{ fontSize: '13px', color: '#666', mb: 2 }}>
          This connector uses environment variables for configuration. Define custom environment
          variables below.
        </Typography>

        {/* Environment Variables Interface (matches old UI manageEnvVars.js) */}
        <ManageEnvironmentVariables
          envName={envName}
          envId={envId}
          envVarsCred={envVarsCred}
          onClose={onSaved}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Heading */}
      <PageHeading title={pageTitle} subtitle={pageSubtitle} />

      {/* Wizard progress indicator */}
      {isWizard && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            px: 1,
          }}
        >
          <WizardStep step={1} label="Connect Source" active={wizardStep === 1} />
          <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
          <WizardStep step={2} label="Connect Destination" active={wizardStep === 2} />
          <Box sx={{ width: 40, height: 1, backgroundColor: '#ccc' }} />
          <WizardStep step={3} label="Create a Recipe" active={wizardStep === 3} />
        </Box>
      )}

      {/* Breadcrumb navigation: envName > Connections > connectorLabel */}
      <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 18 }} />} sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          onClick={onCancel}
          sx={{ fontSize: '14px', color: '#54698d', cursor: 'pointer' }}
        >
          {envName}
        </Link>
        <Link
          component="button"
          underline="hover"
          onClick={onCancel}
          sx={{ fontSize: '14px', color: '#54698d', cursor: 'pointer' }}
        >
          Connections
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {(() => {
            const logoUrl = getConnectorLogoUrl();
            return logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt=""
                sx={{ width: 24, height: 24, objectFit: 'contain' }}
              />
            ) : (
              getConnectorIcon(provider.label || provider.type, provider.type)
            );
          })()}
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
            {provider.label || provider.type}
          </Typography>
        </Box>
      </Breadcrumbs>

      {/* Connection Error Banner (matches old UI red error banner) */}
      {connectionError && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            '& .MuiAlert-message': {
              fontSize: '13px',
              wordBreak: 'break-word',
            },
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setConnectionError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {connectionError}
        </Alert>
      )}

      {/* Main layout: form left (50%) + elevio help panel right (50%) */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Form body - matches legacy .env-section-body padding: 12px 28px 7px 28px */}
        <Box
          sx={{
            flex: '1 1 calc(50% - 8px)',
            minWidth: 0,
            backgroundColor: '#fff',
            mt: 1,
            padding: '12px 28px 7px 28px',
          }}
        >
          {/* Name and Key row (matches Connector.js lines 809-838) */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={labelSx}>Name</Typography>
              <TextField
                value={connectorName}
                onChange={(e) => setConnectorName(e.target.value)}
                size="small"
                required
                fullWidth
                placeholder="Enter connection name"
                sx={{
                  '& .MuiInputBase-input': { fontSize: '13px', py: '6px', px: '12px' },
                  '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                }}
              />
            </Box>
            {provider.type !== 'LocalFile' && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={labelSx}>Key</Typography>
                <TextField
                  value={connectorKey}
                  onChange={(e) => setConnectorKey(e.target.value)}
                  size="small"
                  required
                  fullWidth
                  disabled={isEditMode}
                  placeholder="Auto-generated key"
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '13px', py: '6px', px: '12px' },
                    '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Provider message (matches index.js lines 255-258) */}
          {provider.message && (
            <Typography sx={{ fontSize: '13px', color: '#555', mb: 2 }}>
              {provider.message}
            </Typography>
          )}

          {/* Provider-specific credential section (routed per wizard-framework credSections/index.js) */}
          <CredentialSectionRouter
            provider={provider}
            creds={credFields}
            onChange={handleFieldChange}
            showPass={showPass}
            toggleShowPass={() => setShowPass((v) => !v)}
            envId={envId}
            envName={envName}
            connectorKey={connectorKey}
            existingCredentials={existingCredentials}
            connectionSuccess={connectionSuccess}
            isTesting={isTesting}
          />

          {/* Connection test status */}
          {isTesting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '12px', color: '#666' }}>
                Testing connection...
              </Typography>
            </Box>
          )}
          {connectionSuccess && !isTesting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
              <CheckCircleIcon sx={{ color: '#00853E', fontSize: 22 }} />
              <Typography sx={{ fontSize: '12px', color: '#00853E' }}>
                Connection verified
              </Typography>
            </Box>
          )}

          {/* Default Connection and API Accessible (matches EnvSection lines 633-644) */}
          {provider.type !== 'LocalFile' && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '13px' }}>Make this my default connection</Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isApiAccessible}
                    onChange={(e) => setIsApiAccessible(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '13px' }}>Make this available via API</Typography>
                }
              />
            </Box>
          )}

          {/* Action buttons (matches ConnectorSection.js — no border top) */}
          {/* LocalFile: no Save & Test Connection or Delete — just upload + Cancel */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2.5, pt: 1.5 }}>
            {provider.type !== 'LocalFile' && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  disabled={!isFormValid || isSaving || isTesting}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 'normal',
                  }}
                >
                  {(isSaving || isTesting) && (
                    <CircularProgress size={14} sx={{ color: '#fff', mr: 0.75 }} />
                  )}
                  Save &amp; Test Connection
                </Button>

                {isEditMode && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 'normal' }}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}

            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={onCancel}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 'normal',
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {/* Elevio documentation help panel (matches ConnectorSection.js lines 840-866) */}
        {provider.elevioArticleId != null && provider.elevioArticleId !== 0 && (
          <Box
            sx={{
              flex: '1 1 calc(50% - 8px)',
              minWidth: 0,
              maxHeight: 'calc(100vh - 180px)',
              overflow: 'auto',
              border: '1px solid #dfe0e0',
              padding: '15px',
              backgroundColor: '#f9f9f9',
            }}
          >
            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>
              {provider.label || provider.type} Connector Setup Guide
            </Typography>
            {createElement('elevio-element', {
              'data-type': 'article',
              'data-id': provider.elevioArticleId,
            })}
          </Box>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setDeleteDialogOpen(false);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ fontSize: '16px' }}>Delete Connection</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '13px' }}>
            Are you sure you want to delete the connection &quot;{connectorName}&quot; (
            {connectorKey})? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            sx={{ textTransform: 'none', fontSize: '13px' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
